import { useTranslation } from '@ubique-innovation/react-translations';
import { filter, includes } from 'lodash';
import React from 'react';
import CategoryCheckbox from '../components/CategoryCheckbox';
import DatePicker from '../components/DatePicker';
import ValidCheckbox from '../components/ValidCheckbox';
import CloseBtn from '../images/ic-close.svg';
import {
    Categories,
    CategoryCounts,
    PickedDate,
    QualityCounts,
    Validity,
    Weather,
} from '../types/Gallery';
import * as styles from './Sidebar.css';

const Sidebar = ({
    categories,
    setCategories,
    catCount,
    qualityCount,
    validity,
    setValidity,
    setShowSidebar,
    date,
    setDate,
}: {
    categories: string[];
    catCount: CategoryCounts | undefined;
    qualityCount: QualityCounts | undefined;
    setCategories: (d: string[]) => void;
    validity: Validity;
    setValidity: (d: Validity) => void;
    setShowSidebar: (d: boolean) => void;
    date: PickedDate;
    setDate: (d: PickedDate) => void;
}): React.ReactElement => {
    const { t } = useTranslation();

    const filterCategories = (
        e: React.ChangeEvent<HTMLInputElement>,
        weather: string,
    ): void => {
        if (e.target.checked) {
            const e = [...categories];
            e.push(weather);
            setCategories(e);
        } else {
            const e = [...categories];
            const f = filter(e, (e) => e !== weather);
            setCategories(f);
        }
    };

    return (
        <div className={styles.sidebar} id="sidebar">
            <div className={styles.sidebarHeader}>
                <div>
                    <h1 className={styles.meteoTitle}>{t('app.title')}</h1>
                </div>
                <button
                    onClick={() => setShowSidebar(false)}
                    className={styles.closeBtn}
                >
                    <img src={CloseBtn} alt="close-btn" />
                </button>
            </div>
            <DatePicker date={date} setDate={setDate} />
            <h2>Filter</h2>
            <h3 className={styles.title}>Gültigkeit</h3>
            <p className={styles.description}>
                Alle Meldungen, mit und ohne Bild
            </p>
            <div
                className={
                    qualityCount
                        ? styles.validContainer.default
                        : styles.validContainer.skelton
                }
            >
                <ValidCheckbox
                    label="Alle"
                    checked={validity === Validity.ALL}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                            setValidity(Validity.ALL);
                        }
                    }}
                    totalNumber={qualityCount ? qualityCount.total : undefined}
                />
                <ValidCheckbox
                    label="Gültig"
                    checked={validity === Validity.VALID}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                            setValidity(Validity.VALID);
                        }
                    }}
                    totalNumber={qualityCount?.valid}
                />
                <ValidCheckbox
                    label="Ungültig"
                    checked={validity === Validity.INVALID}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                            setValidity(Validity.INVALID);
                        }
                    }}
                    totalNumber={qualityCount?.invalid}
                />
            </div>
            <div className={styles.divider}></div>
            <h3 className={styles.title}>Kategorien</h3>
            <div className={styles.description}>
                <div className={styles.number}>2/4</div> → 2 von 4 Meldungen
                haben ein Bild
            </div>

            <CategoryCheckbox
                icon=""
                label="Alle "
                checked={categories === Categories}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                        setCategories(Categories);
                    } else {
                        setCategories([]);
                    }
                }}
                imageNumber={
                    catCount
                        ? `${catCount.ALLE.withImg}/${catCount.ALLE.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="rain"
                label="Regen"
                checked={includes(categories, Weather.REGEN)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.REGEN);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.REGEN.withImg}/${catCount.REGEN.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="cloudiness"
                label="Bewölkung"
                checked={includes(categories, Weather.BEWOELKUNG)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.BEWOELKUNG);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.BEWOELKUNG.withImg}/${catCount.BEWOELKUNG.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="wind"
                label="Wind"
                checked={includes(categories, Weather.WIND)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.WIND);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.WIND.withImg}/${catCount.WIND.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="hail"
                label="Hagel"
                checked={includes(categories, Weather.HAGEL)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.HAGEL);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.HAGEL.withImg}/${catCount.HAGEL.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="lightning"
                label="Blitze"
                checked={includes(categories, Weather.BLITZE)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.BLITZE);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.BLITZE.withImg}/${catCount.BLITZE.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="fog"
                label="Nebel"
                checked={includes(categories, Weather.NEBEL)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.NEBEL);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.NEBEL.withImg}/${catCount.NEBEL.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="snow cover"
                label="Schneefall"
                checked={includes(categories, Weather.SCHNEEFALL)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.SCHNEEFALL);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.SCHNEEFALL.withImg}/${catCount.SCHNEEFALL.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="snow"
                label="Schneedecke"
                checked={includes(categories, Weather.SCHNEEDECKE)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.SCHNEEDECKE);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.SCHNEEDECKE.withImg}/${catCount.SCHNEEDECKE.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="icy conditions"
                label="Glätte"
                checked={includes(categories, Weather.GLAETTE)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.GLAETTE);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.GLAETTE.withImg}/${catCount.GLAETTE.total}`
                        : undefined
                }
            />
            <CategoryCheckbox
                icon="tornado"
                label="Tornado"
                checked={includes(categories, Weather.TORNADO)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    filterCategories(e, Weather.TORNADO);
                }}
                imageNumber={
                    catCount
                        ? `${catCount.TORNADO.withImg}/${catCount.TORNADO.total}`
                        : undefined
                }
            />
        </div>
    );
};

export default Sidebar;
